import { Icon36CoinsStacks2Outline, Icon36Like, Icon28CoinsOutline } from "@vkontakte/icons";
import { useRouteNavigator } from "@vkontakte/vk-mini-apps-router";
import { Accordion, Card, Header, Spacing, Spinner } from "@vkontakte/vkui";
import React, { useEffect, useState } from 'react';
import Countdown from "../../components/Countdown/Countdown";
import { add3DailyAttempts, getDailyAttemptsPrice, getPlayerInfo } from "../../services/player";
import { getActiveGame } from "../../services/game";
import BonusStore from "../../store/bonus";
import GameStore from "../../store/game";
import { timeToMidnight } from "../../utils/time";
import { numberWordSuffix } from "../../utils/word";
import { Add3DailyAttemptsBonusPrice } from "../../model/prices";
import { useSnackbar } from "../../context/SnackbarContext";
import { observer } from "mobx-react-lite";
import { BUY_COINS_MODAL } from "../../routing/routing";

// StartGame - кнопка, которая позволяет начать игру на главной странице
const StartButton = observer(({ handleButton }) => {
    const routeNavigator = useRouteNavigator();
    const [player, setPlayer] = useState({});
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(true);
    const [isActiveGameLoading, setIsActiveGameLoading] = useState(true);
    // Активная игра существует, если есть валидный идентификатор и флаг активности
    const hasRunningGame = GameStore.isActive && Number.isFinite(Number(GameStore.gameID)) && Number(GameStore.gameID) > 0;
    const [dailyAttemptPrice, setDailyAttemptPrice] = useState(Add3DailyAttemptsBonusPrice);
    const availableAttempts = player.available_daily_attempts ?? player.daily_attempts ?? 0;
    const purchasesToday = player.daily_attempts_purchase_count ?? 0;
    const showPriceResetDisclaimer = purchasesToday > 0;
    const fetchDailyAttemptPrice = () => {
        getDailyAttemptsPrice().then(resp => {
            const price = resp?.data?.response?.price;
            if (Number.isFinite(price)) {
                setDailyAttemptPrice(price);
            }
        }).catch(err => {
            console.error("Не удалось получить цену покупки жизней:", err);
            setDailyAttemptPrice(Add3DailyAttemptsBonusPrice);
        });
    };

    // Запрашивает активную игру и синхронизирует статус кнопки
    const fetchActiveGame = () => {
        setIsActiveGameLoading(true);
        getActiveGame().then(resp => {
            const data = resp?.data?.response ?? resp?.data;
            if (!data) {
                GameStore.setIsActive(false);
                GameStore.setGameID("");
                return;
            }

            if (data.isActive) {
                const gameId = data.game?.gameId ?? data.game?.id;
                if (Number.isFinite(Number(gameId)) && Number(gameId) > 0) {
                    GameStore.setGameID(gameId);
                }
                GameStore.setIsActive(true);
                return;
            }

            GameStore.setIsActive(false);
            GameStore.setGameID("");
        }).catch(err => {
            console.error("Не удалось получить активную игру:", err);
        }).finally(() => {
            setIsActiveGameLoading(false);
        });
    };

    useEffect(() => {
        // Получаем данные игрока
        getPlayerInfo().then(resp => {
            const player = resp.data.response;
            setPlayer(player);
            BonusStore.setCoins(player.coins);
            const totalDailyAttempts = player.available_daily_attempts ?? player.daily_attempts ?? 0;
            GameStore.setDailyAttempts(totalDailyAttempts);
        }).catch(err => {
            console.error("Не удалось получить информацию об игроке: ", err);
        }).finally(() => {
            setIsLoading(false);
        });

        fetchDailyAttemptPrice();
        fetchActiveGame();
    }, []);

    // Обрабатывает нажатие на кнопку "Играть" за монеты
    const playButtonHandler = () => {
        if (handleButton) {
            handleButton();
        }

        // Чистим состояние игры только при старте новой сессии
        if (!hasRunningGame) {
            GameStore.flush();
        }

        // Переходим на страницу с игрой
        routeNavigator.push('/game');
    };

    // Обрабатывает нажатие на кнопку "Купить жизни" за монеты
    const addNewDailyAttemptsHandler = () => {
        if (BonusStore.coins < dailyAttemptPrice) {
            showErrorSnackbar("Не хватает монет");
            return;
        }

        add3DailyAttempts().then(resp => {
            const player = resp.data.response;
            setPlayer(player);
            BonusStore.setCoins(player.coins);
            const totalDailyAttempts = player.available_daily_attempts ?? player.daily_attempts ?? 0;
            GameStore.setDailyAttempts(totalDailyAttempts);
            fetchDailyAttemptPrice();

            showSuccessSnackbar("Жизни восстановлены");
        });
    };

    const isInitialLoading = isLoading || isActiveGameLoading;
    const isPlayButtonDisabled = isInitialLoading;
    const playButtonClassName = "flex justify-center items-center py-2 rounded-lg border-solid border-2 w-3/4 font-medium " +
        (isPlayButtonDisabled
            ? "cursor-default bg-gray-300 hover:bg-gray-300 border-gray-500/70 text-gray-700"
            : "cursor-pointer app-btn-primary");

    return (<div className={"flex w-full flex-col"}>
        <div className="w-full bg-gray-200 rounded-xl border border-gray-300/50 shadow-sm">
            <Header>
                <div className={"flex items-center gap-2"}>
                    <span className={"text-gray-800"}>Новая игра</span>
                    {isInitialLoading && <Spinner size={"small"} />}
                </div>
            </Header>

            {/* Плейсхолдеры сохраняют высоту блока и убирают мерцание во время загрузки */}
            {isLoading && (
                <div className={"px-4 pb-4"}>
                    <div className={"animate-pulse flex flex-col items-center gap-3"}>
                        <div className={"h-10 w-3/4 rounded bg-gray-400/50"}></div>
                        <div className={"h-3 w-1/2 rounded bg-gray-400/40"}></div>
                        <div className={"h-12 w-full max-w-sm rounded bg-gray-400/35"}></div>
                    </div>
                </div>
            )}

            {!isLoading && <>
                {(hasRunningGame || availableAttempts > 0) &&
                    <>
                        <Spacing size={12} />
                        <div className={"flex justify-center items-center flex-row gap-1"}>
                            <div
                                className={playButtonClassName}
                                onClick={isPlayButtonDisabled ? undefined : playButtonHandler}>
                                {hasRunningGame && "Продолжить игру"}
                                {!hasRunningGame && "Начать новую игру"}
                            </div>
                        </div>

                        <div className={"text-center text-xs text-gray-800 mt-2"}>
                            Запустите новый раунд и соберите новый рекорд.
                        </div>

                        <Spacing size={12} />

                        <BalancePanel player={player} availableAttempts={availableAttempts} />
                    </>
                }

                {(!hasRunningGame && availableAttempts === 0) &&
                    <>
                        <Countdown date={timeToMidnight()} variant="light" />

                        <div className={"flex justify-center items-center flex-col gap-1"}>
                            <div
                                className={"text-center whitespace-normal break-words leading-5 px-3 py-1 rounded-lg border-solid border-2 w-3/4 font-medium " + (BonusStore.coins < dailyAttemptPrice ? "cursor-default bg-gray-300 hover:bg-gray-300 border-gray-500/70 text-gray-700" : "cursor-pointer app-btn-primary")}
                                onClick={addNewDailyAttemptsHandler}>
                                <div className="text-sm">Пополнить жизни</div>
                                <div className="text-xs">
                                    3 <span className="leading-none">❤️</span> за {dailyAttemptPrice} {numberWordSuffix(dailyAttemptPrice, ["монету", "монеты", "монет"])}
                                </div>
                            </div>
                        </div>
                        {showPriceResetDisclaimer &&
                            <div className={"mt-2 text-xs text-gray-800 text-center"}>
                                Завтра стоимость снова упадёт до {Add3DailyAttemptsBonusPrice} монет.
                            </div>
                        }

                        <BalancePanel player={player} padding={"mt-2"} availableAttempts={availableAttempts} />
                    </>
                }
            </>}
        </div>
    </div>);
})

export default StartButton;

// BalancePanel отображает панель с балансом и жизнями игрока
const BalancePanel = observer(({ player, padding, availableAttempts }) => {
    const routeNavigator = useRouteNavigator();

    const handleBuyCoinsClick = () => {
        routeNavigator.showModal(BUY_COINS_MODAL);
    };

    const lives = availableAttempts ?? player.available_daily_attempts ?? player.daily_attempts ?? 0;

    return <>
        <div className={"flex justify-center items-center p-2 text-gray-800 " + padding}>
            {/* Основная панель с балансом */}
            <div className={"flex items-center justify-center w-full max-w-sm p-3"}>
                {/* Секция монет с кнопкой покупки */}
                <div className={"flex items-center"}>
                    <div className={"flex flex-row items-center justify-center"}>
                        <Icon36CoinsStacks2Outline className={"text-yellow-500 mr-1"} height={18} width={18} />
                        <div className={"text-sm font-semibold text-gray-800 mr-1"}>{BonusStore.coins}</div>
                        <div className={"text-xs text-gray-800"}>{numberWordSuffix(BonusStore.coins, ["монета", "монеты", "монет"])}</div>
                    </div>

                    {/* Кнопка покупки монет рядом с балансом */}
                    <div
                        className={"flex items-center justify-center ml-2 p-1.5 rounded-md border cursor-pointer transition-all duration-200 group app-btn-primary"}
                        onClick={handleBuyCoinsClick}
                        title="Купить монеты"
                    >
                        <Icon28CoinsOutline className={"text-white transition-colors"} height={14} width={14} />
                        <span className={"text-white ml-1 text-xs font-medium transition-colors"}>
                            Купить
                        </span>
                    </div>
                </div>

                {/* Разделитель */}
                <div className={"w-px h-8 bg-gray-300 mx-4"}></div>

                {/* Секция жизней */}
                <div className={"flex items-center"}>
                    <Icon36Like className={"text-brand-danger"} height={18} width={18} />
                    <div className={"ml-2 flex flex-row items-center justify-center"}>
                        <div className={"text-sm font-semibold mr-1"}>{lives}</div>
                        <div className={"text-xs"}>{numberWordSuffix(lives, ["жизнь", "жизни", "жизней"])}</div>
                    </div>
                </div>
            </div>
        </div>
    </>;
});
